/**
 * PlexCredentialModal
 *
 * Collects plex.tv Email, Password, and optional 2FA code before a Plex update.
 * The backend requires these to authenticate the PlexPass download.
 *
 * Security contract:
 *   - Password and OTP are NEVER logged to the console
 *   - Values are NEVER written to localStorage or sessionStorage
 *   - Credentials are sent once via POST body then discarded
 *   - Closing without submitting creates no job
 */
import { type FormEvent, type KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Input } from "../ui/Input";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";

// ── Focus trap ────────────────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const trapFocus = (container: HTMLElement, event: KeyboardEvent<HTMLDivElement>) => {
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first?.focus();
  }
};

// ── Types ─────────────────────────────────────────────────────────────────────

export type PlexCredentials = {
  plexEmail: string;
  plexPassword: string;
  plexOtp: string;
};

type PlexCredentialModalProps = {
  open: boolean;
  /** Called with credentials when the user submits. Callee fires the update request. */
  onSubmit: (credentials: PlexCredentials) => Promise<void>;
  /** Called when the user cancels or dismisses without submitting. */
  onClose: () => void;
};

// ── Component ─────────────────────────────────────────────────────────────────

export const PlexCredentialModal = ({ open, onSubmit, onClose }: PlexCredentialModalProps) => {
  const titleId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus email on open
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      emailRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Reset state when modal opens so re-triggers start fresh
  useEffect(() => {
    if (open) {
      setEmail("");
      setPassword("");
      setOtp("");
      setShowPassword(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  // Dismiss on Escape when not submitting
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, submitting, onClose]);

  // Tab focus trap — keep focus within the dialog panel
  const handleDialogKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Tab" && dialogRef.current) {
        trapFocus(dialogRef.current, e);
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !password) return;

      setSubmitting(true);
      setError(null);

      try {
        await onSubmit({ plexEmail: email.trim(), plexPassword: password, plexOtp: otp.trim() });
        // Modal is closed by parent after a successful job creation
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to start update";
        setError(msg);
        setSubmitting(false);
      }
    },
    [email, password, otp, onSubmit],
  );

  const handleClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  if (!open) return null;

  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleDialogKeyDown}
        className={cn(
          "w-full max-w-md rounded-xl border border-border/50 bg-card shadow-xl",
          "animate-in fade-in-0 zoom-in-95 duration-200",
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
              <Icon icon="lucide:lock" className="h-4 w-4 text-amber-500" aria-hidden="true" />
            </div>
            <div>
              <h2 id={titleId} className="text-base font-semibold text-foreground">
                Plex.tv Sign-in Required
              </h2>
              <p className="text-xs text-muted-foreground">
                PlexPass downloads require authentication
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close dialog"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={(e) => { void handleSubmit(e); }} className="px-5 py-4 space-y-4">
          {/* Privacy notice */}
          <div className="flex items-start gap-2 rounded-lg bg-muted/30 border border-border/30 px-3 py-2.5">
            <Icon icon="lucide:shield-check" className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              These credentials are used only for this update and are not stored or shared outside
              of this instance.
            </p>
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label htmlFor="plex-email" className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Plex.tv Email <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <Input
              ref={emailRef}
              id="plex-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={submitting}
              required
            />
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label htmlFor="plex-password" className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Password <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <div className="relative">
              <Input
                id="plex-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your plex.tv password"
                disabled={submitting}
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={submitting}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 rounded-sm"
              >
                <Icon
                  icon={showPassword ? "lucide:eye-off" : "lucide:eye"}
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          {/* 2FA code — optional */}
          <div className="space-y-1.5">
            <label htmlFor="plex-otp" className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Two-Factor Code{" "}
              <span className="normal-case font-normal text-muted-foreground/60">(leave blank if no 2FA)</span>
            </label>
            <Input
              id="plex-otp"
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit code (optional)"
              disabled={submitting}
              maxLength={16}
            />
          </div>

          {/* Error */}
          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
              <Icon icon="lucide:alert-circle" className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!canSubmit}
              className="gap-1.5"
            >
              {submitting && <Spinner size="4" />}
              {submitting ? "Starting update…" : "Update Plex"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
